"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Trophy, Sword, Shield, ChevronRight } from "lucide-react"
import Link from "next/link"

export function TopRankings() {
    const [rankings, setRankings] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchRankings = async () => {
            try {
                const res = await fetch("/api/ranking?type=pvp&limit=5")
                if (res.ok) {
                    const data = await res.json()
                    setRankings(data.rankings || [])
                }
            } catch (error) {
                console.error("Failed to fetch rankings")
            } finally {
                setLoading(false)
            }
        }
        fetchRankings()
    }, [])

    if (loading && rankings.length === 0) return null

    return (
        <section className="py-24 bg-slate-950/50 backdrop-blur-sm border-y border-slate-900">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    {/* Left: Branding & CTA */}
                    <div className="lg:col-span-5 space-y-6">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-500 text-[10px] font-black uppercase tracking-[0.2em]">
                            <Trophy className="w-3 h-3" />
                            Hall of Fame
                        </div>
                        <h2 className="text-5xl font-black text-white tracking-tighter leading-none uppercase">
                            Supreme <br />
                            <span className="text-amber-500">Challengers</span>
                        </h2>
                        <p className="text-slate-400 text-lg leading-relaxed max-w-md italic">
                            The strongest warriors in Aden have carved their names in blood.
                            Will you be the next to join them?
                        </p>
                        <div className="pt-4">
                            <Link href="/ranking" className="group inline-flex items-center gap-4 px-6 py-3 bg-slate-900 border border-slate-800 rounded-xl hover:border-amber-500/50 transition-all">
                                <span className="text-white font-bold text-xs uppercase tracking-widest">Full Leaderboards</span>
                                <div className="h-2 w-2 rounded-full bg-amber-500 group-hover:scale-150 transition-transform" />
                            </Link>
                        </div>
                    </div>

                    {/* Right: Ranking Cards */}
                    <div className="lg:col-span-7 space-y-3">
                        {rankings.map((player, idx) => (
                            <motion.div
                                key={player.char_name}
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                className="group relative flex items-center gap-4 p-4 bg-slate-900/40 border border-slate-800/40 rounded-2xl hover:bg-slate-800/40 hover:border-amber-500/30 transition-all cursor-default"
                            >
                                {/* Rank Number */}
                                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center font-black text-2xl italic tracking-tighter text-slate-800 group-hover:text-amber-500/20 transition-colors">
                                    0{idx + 1}
                                </div>

                                {/* Avatar Placeholder */}
                                <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-500 group-hover:text-amber-500 transition-colors">
                                    <Shield className="w-6 h-6" />
                                </div>

                                {/* Info */}
                                <div className="flex-1">
                                    <h4 className="font-bold text-white group-hover:text-amber-400 transition-colors uppercase tracking-tight">
                                        {player.char_name}
                                    </h4>
                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                        Level {player.level} • <span className="text-amber-500/70">Berserker</span>
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="text-right">
                                    <div className="flex items-center gap-2 justify-end text-white font-black text-xl tabular-nums">
                                        {player.pvpkills}
                                        <Sword className="w-4 h-4 text-red-500" />
                                    </div>
                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                        PvP Victories
                                    </div>
                                </div>

                                {/* Highlight for Top 1 */}
                                {idx === 0 && (
                                    <div className="absolute -top-2 -right-2 bg-amber-500 text-slate-950 p-1.5 rounded-lg shadow-lg shadow-amber-500/20">
                                        <Trophy className="w-4 h-4" />
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
